<template>
  <div :id="uuid" class="element-valuebar" :title="valueVisible ? (formattedFullValue + ' ' + unit) : ''">
    <div class="element-valuebar__bar">
      <div v-show="mounted" class="element-valuebar__fill" :style="[barColorStyle]"></div>

      <!--<div class="element-valuebar__oldfill" :style="[barColorStyle]"></div>-->
    </div>
    <div class="element-valuebar__valueblock" v-if="valueVisible">
      <slot name="value" :value="props.value == null ? null : tweenedValue" :formattedValue="formattedValue">
        <span class="element-valuebar__value">{{ formattedValue }}</span>
      </slot>
      <slot name="unit" :unit="unit" :invalid="unitInvalid">
        <span class="element-valuebar__unit" :class="{ '-invalid': unitInvalid }"
          >{{ unit }}</span
        >
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { v4 } from "uuid";

import { useSettingsStore } from "@editor/store/settings.store";
import { formatNumber } from "@/helpers/utility/Formatting";
import { useTransition } from "@vueuse/core";

const props = withDefaults(
  defineProps<{
    value: number | boolean | null;
    minValue: number;
    maxValue: number;
    unit: string;
    unitInvalid?: boolean;
    valueVisible?: boolean;
  }>(),
  {
    unitInvalid: false,
    valueVisible: true
  }
);

const uuid = v4();
const mounted = ref(false);

onMounted(() => {
  mounted.value = true;
});

const tweenedValue = useTransition(() => Number(props.value ?? 0), {
  duration: 200
});

/**
 * Absolute value range between min and max value.
 */
const valueRange = computed(() => {
  return props.maxValue - props.minValue;
});

const decimalPoints = computed(() => {
  // get the number of decimal points from the max and min value
  let n = Math.max(
    (props.maxValue + "").split(".")[1]?.length ?? 0,
    (props.minValue + "").split(".")[1]?.length ?? 0
  );
  const distance = Math.abs(props.maxValue - props.minValue);
  if (n != 0) return Math.min(n, 2);
  if (distance <= 10) return 2;
  if (distance < 100) return 1;
  return 0;
});

const barColorStyle = computed(() => {
  if (colors.value.length === 0) return "";
  const colorIndex =
    Math.ceil((currentPercent.value / 100) * colors.value.length) - 1;
  return {
    color: colors.value[colorIndex]
  };
});

const colors = computed(() => {
  return useSettingsStore().targetValueColors;
});

const formattedValue = computed(() => {
  if (props.value == null) {
    return formatNumber(props.value, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimalPoints.value
    });
  }
  return formatNumber(tweenedValue.value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimalPoints.value
  });
});

const formattedFullValue = computed(() => {
  return formatNumber(props.value);
});

const currentPercent = computed(() => {
  if (props.value == null) return 0;
  if (props.value == Infinity) return 100;
  if (props.value == -Infinity) return 0;
  if (valueRange.value === 0) return 100;
  const value = Number(props.value);
  if (isNaN(value)) return 0;
  return ((value - props.minValue) / valueRange.value) * 100;
});
</script>

<style lang="scss">
.element-valuebar {
  width: 100%;
  display: grid;
  align-items: center;
  justify-content: center;
  grid-template-columns: 1fr min-content;
  gap: min(0.75em, 12px);
  font-size: 0.875em;
  
  &.-width-limited {
    max-width: 14em;
  }

  --current-value: v-bind(currentPercent);
  --current-percent: calc(var(--current-value) * 1%);

  &__bar {
    position: relative;
    width: 100%;
    flex-shrink: 2;
    height: calc(1em - 1px);
    min-width: 0;

    border-radius: 50em / 40em;
    border: 1px solid var(--color-border);
    overflow: hidden;
    grid-column: 1 / 2;
    grid-row: 1 / 2;
    background-color: var(--color-background-deep);
  }

  &__fill,
  &__oldfill {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transform-origin: center left;
    background-color: currentColor;
    transition:
      transform 250ms ease-in-out,
      color 250ms;
  }
  &__fill {
    transform: scaleX(var(--current-percent));
    background-image: linear-gradient(
      rgba(255, 255, 255, 0.4),
      rgba(255, 255, 255, 0.4)
    );
  }
  &__oldfill {
    --old-value: v-bind(currentPercent);
    --old-percent: calc(var(--old-value) * 1%);
    transform: scaleX(var(--old-percent));
    opacity: 0.2;
  }

  &__valueblock {
    text-align: center;
    flex-shrink: 0;
    white-space: nowrap;
    font-size: calc(1em + 5px);
    min-width: 2.5em;
    display: grid;
    grid-template-columns: max-content max-content;
    white-space: nowrap;
    align-items: baseline;
  }
  &__value {
    display: inline-block;
    text-align: right;
    font-size: 1em;
    line-height: 0.75em;
    padding-right: 0.125em;
    min-width: 1.25em;
  }
  &__unit {
    font-size: 0.75em;

    &:where(.-invalid) {
      text-underline-offset: 3px;
      text-decoration: wavy underline;
      text-decoration-color: rgba(var(--tint-contrast), 0.25);
      text-decoration-thickness: 1px;
    }
  }
}
</style>
